import { ArrowRight } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Field, Steps, NextStep, Callout, RoleBadge, pathFor,
  Diagram, MockSelect, MockField, MockButton, MockRow,
} from "../shared";

export default function FormsHandoffSection() {
  return (
    <div data-testid="guide-forms-handoff">
      <PageHeading
        icon={ArrowRight}
        title="Hand Off Responsibility"
        description="Pass a task to someone else to act on next — without changing who owns it. Use it whenever the next step is somebody else's job."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="admin" />
        <span className="text-xs text-white/40">General Admin and above.</span>
      </div>

      <Callout variant="info" title="In plain words">
        A handoff just moves <span className="text-white">who is holding the task right now</span> ("Currently With"). The owner — the person accountable from start to finish — stays the same.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-sm text-white/65 space-y-1.5 ml-2">
          <li>When the next bit of work needs a different person to do it.</li>
          <li>When the current holder is away and someone else must pick it up.</li>
          <li>To send a task into a review stage, use <Link href={pathFor("forms/send-for-review")} className="text-cyan-300 hover:underline">Send for Review</Link> instead — see the warning below.</li>
        </ul>
      </Section>

      <Section title="What the form looks like">
        <Diagram
          frameLabel="Action Item Detail → Hand off"
          legend={[
            <>Pick who should hold the task next from the <span className="text-white">Hand to</span> dropdown.</>,
            <>Optionally add a <span className="text-white">note</span> telling them what to do.</>,
            <>Optionally set a <span className="text-white">due back by</span> date (your own internal deadline).</>,
            <>Click <span className="text-white">Hand Off</span> to pass it on.</>,
          ]}
        >
          <MockSelect
            label="Hand to"
            value="Sara K."
            marker={1}
            open
            options={["Sara K.", "Ravi P.", "Internal Audit Committee"]}
          />
          <MockField label="Note" placeholder="Please review section 4 — assumptions are in the appendix." marker={2} />
          <MockField label="Due back by" value="Thu 26 Jun" marker={3} />
          <MockButton label="Hand Off" marker={4} />
        </Diagram>
      </Section>

      <Section title="Step-by-step">
        <Steps
          steps={[
            <>Open the item's detail page, and find the <span className="text-white">Hand to</span> dropdown next to the status selector (or use the inline handoff control on an Updates page row). Pick the person.</>,
            <>Add a <span className="text-white">note</span> so the next person knows what's expected.</>,
            <>If you want it back by a certain time, set a <span className="text-white">due back by</span> date.</>,
            <>Click <span className="text-white">Hand Off</span>.</>,
          ]}
        />
      </Section>

      <Section title="Every box explained">
        <Field name="Hand to" required="required">
          <p>Who should hold the item next. The dropdown lists active <span className="text-white">team members</span> and any <span className="text-white">authority lane names</span> that are real bodies (like an internal committee).</p>
          <p className="text-white/40">Pick a person if the next action is owned by an individual.</p>
        </Field>

        <Field name="Note" required="optional">
          <p>What the next person needs to do, plus any context. Recommended for clarity.</p>
          <p className="text-white/40">Example: "Please review section 4 — assumptions are in the appendix. Need feedback by Thursday."</p>
        </Field>

        <Field name="Combined Status Change" required="optional">
          <p>On the detail page you can change the status at the same time as the handoff, and one record covers both.</p>
          <p className="text-white/40">For the common case of pushing into MD review, use the dedicated <span className="text-white">Send for Review</span> button on the Updates page instead — it does both in one click.</p>
        </Field>

        <Field name="Due back by" required="optional">
          <p>An <span className="text-white">internal deadline for the receiver</span> — when you'd like the task back in your hands or in the next stage's queue.</p>
          <p className="text-white/40">Recorded on the event, shown on the handoff email and the Responsibility Timeline. It does <em>not</em> change the official due date and does not send reminders. See <Link href={pathFor("handoff-deadlines")} className="text-cyan-300 hover:underline">Handoff Deadlines &amp; Fairness</Link>.</p>
        </Field>

        <Field name="Why this internal date?" required="optional">
          <p>A short reason for the internal deadline you set. Helps the receiver understand the context.</p>
          <p className="text-white/40">Examples: "I need 2 days to review before the board pack closes.", "Quick sanity check — anything by Friday is fine."</p>
        </Field>
      </Section>

      <Section title="Handing off a task that someone else is holding">
        <p className="text-white/60">
          If you hand off a task that is currently with another person (not you), the system asks you to pick a <span className="text-white">reason category</span> before the change goes through. This is recorded for governance review.
        </p>
        <ul className="list-disc list-inside space-y-1 ml-2 mt-2 text-white/60">
          <li><span className="text-white">Reassigned to correct owner</span> — the first assignment was wrong.</li>
          <li><span className="text-white">Current holder unavailable</span> — leave, travel, or absence.</li>
          <li><span className="text-white">Workload rebalancing</span>.</li>
          <li><span className="text-white">Change in scope or priorities</span>.</li>
          <li><span className="text-white">Insufficient progress</span> — non-responsive or stalled. <em>Note required.</em> Only this reason counts toward the displaced holder's "Pulled (Stalled)" KPI metric. <span className="text-[#FF9F43]">Restricted</span>: only users with the <span className="font-mono text-white">confirm_dropped_the_ball</span> permission (C-suite role and Super Admin by default) can pick this directly. Everyone else sees the row locked and uses the escalation option below.</li>
          <li><span className="text-white">Escalate as dropped-the-ball — pending senior review</span>. <em>Note + C-suite reviewer required.</em> Use when you suspect dropped-the-ball behaviour but don't have authority to flag it yourself. The reassignment happens immediately so work doesn't stall, and the named C-suite reviewer is emailed and gets an inbox row at <span className="font-mono text-white">/escalations</span> where they confirm or dismiss the KPI flag. The displaced holder receives only the standard neutral reassignment email — no mention of the escalation, by design. The KPI metric only increments if the reviewer clicks Confirm; dismissed escalations leave no trace.</li>
          <li><span className="text-white">Other</span> — please specify. <em>Note required.</em></li>
        </ul>
        <p className="text-white/40 mt-2">
          The audit trail and Responsibility Timeline everyone can see show only "reassigned from X to Y at TIME". The reason category is hidden from regular users and visible only to <span className="text-white">Super Admin</span> and <span className="text-white">Dev Admin</span>.
        </p>
      </Section>

      <Callout variant="warning" title="Ceremonial actors are NOT in this dropdown">
        The Hand to dropdown deliberately leaves out <span className="text-white">SDF MD</span>, <span className="text-white">SDF PMO</span>, <span className="text-white">SDF Board Secretary</span>, <span className="text-white">EDGE Chairman</span>, <span className="text-white">EDGE MD Office</span>, and <span className="text-white">Board / ExCom</span>. These are workflow stages, not people. Assigning items to them by plain handoff used to leave them stuck (no status change → never on the MD Meetings queue). To route an item into one of those stages, use <span className="text-white">Send for Review</span> on the Updates page or the status transition on the detail page — both pair the destination with the correct receiving status.
      </Callout>

      <NextStep>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>The "Currently With" pointer updates immediately. The owner does not change.</li>
          <li>Event <span className="text-white">RESPONSIBILITY_HANDOFF</span> records the previous holder, the new holder, your note, and the time.</li>
          <li>The receiving person gets an email with the item details and your note.</li>
          <li>On Summary, the item now shows <span className="text-[#FF9F43]">"With: [new name]"</span>.</li>
          <li>If you changed the status too, a separate <span className="text-white">STATUS_CHANGE</span> event is also recorded.</li>
        </ul>
      </NextStep>

      <Callout variant="info" title="Handoff vs status change">
        A handoff alone changes who holds the item. A status change alone moves it to a new stage. They're independent — but you can do both in one form to keep the record tidy.
      </Callout>
    </div>
  );
}
